/**
 * Hero component - Prominent banner section for key messaging
 *
 * A versatile hero section that supports various layouts,
 * backgrounds, and content arrangements following the
 * Penticton.dev design vision.
 */

import {
  Box,
  Typography,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  SxProps,
  ButtonProps,
} from '@mui/material';
import { ReactNode, forwardRef } from 'react';
import { useAppTheme } from '../../app/providers/ThemeProvider';
import { getBrandGradient } from '../../theme/tokens';
import { Section, SectionProps } from './Section';

export interface HeroAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Button variant */
  variant?: ButtonProps['variant'];
  /** Button color */
  color?: ButtonProps['color'];
  /** Button size */
  size?: ButtonProps['size'];
  /** Whether button is disabled */
  disabled?: boolean;
  /** Button start icon */
  startIcon?: ReactNode;
  /** Button end icon */
  endIcon?: ReactNode;
}

export interface HeroProps {
  /** Main headline */
  title: string;
  /** Supporting subtitle/description */
  subtitle?: string;
  /** Call-to-action buttons */
  actions?: HeroAction[];
  /** Background variant */
  backgroundVariant?: 'default' | 'gradient' | 'brand' | 'subtle' | 'image' | 'video';
  /** Background image URL (when using 'image' variant) */
  backgroundImage?: string;
  /** Background video URL (when using 'video' variant) */
  videoSrc?: string;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Layout variant */
  variant?: 'default' | 'compact' | 'feature';
  /** Additional content to render */
  children?: ReactNode;
  /** Custom sx props */
  sx?: SxProps;
  /** Section props to pass through */
  sectionProps?: Partial<SectionProps>;
}

/**
 * Hero banner component
 */
export const Hero = forwardRef<HTMLElement, HeroProps>(
  (
    {
      title,
      subtitle,
      actions = [],
      backgroundVariant = 'default',
      backgroundImage,
      videoSrc,
      align = 'center',
      variant = 'default',
      children,
      sx,
      sectionProps = {},
      ...props
    },
    ref,
  ) => {
    const theme = useAppTheme();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    // Background style variants
    const backgroundStyles = {
      default: {
        backgroundColor: 'background.paper',
      },
      gradient: {
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: theme.palette.primary.contrastText,
      },
      brand: {
        background: getBrandGradient(
          'primary',
          muiTheme.palette.mode as 'light' | 'dark',
        ),
        color:
          muiTheme.palette.mode === 'light'
            ? '#ffffff'
            : theme.customTokens.colors.neutral[900],
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.1) 0%, transparent 60%)',
          zIndex: 1,
        },
        '& > *': {
          position: 'relative',
          zIndex: 2,
        },
      },
      subtle: {
        background: getBrandGradient('subtle', muiTheme.palette.mode as 'light' | 'dark'),
        color: 'inherit',
        position: 'relative',
      },
      image: {
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: getBrandGradient(
            'overlay',
            muiTheme.palette.mode as 'light' | 'dark',
          ),
          zIndex: 1,
        },
        '& > *': {
          position: 'relative',
          zIndex: 2,
        },
        color: 'white',
      },
      video: {
        position: 'relative',
        overflow: 'hidden',
        color: 'white',
        minHeight: { xs: '600px', sm: '700px', md: '70vh' },
        py: { xs: 10, sm: 12, md: 16 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.75) 0%, rgba(13, 71, 161, 0.75) 100%)',
          zIndex: 2,
        },
        '& > *': {
          position: 'relative',
          zIndex: 3,
        },
      },
    };

    // Layout variants
    const layoutVariants = {
      default: {
        spacing: { xs: 3, md: 4 },
        titleVariant: isMobile ? 'h3' : 'h1',
        subtitleVariant: isMobile ? 'h6' : 'h5',
      },
      compact: {
        spacing: { xs: 2, md: 3 },
        titleVariant: isMobile ? 'h4' : 'h2',
        subtitleVariant: 'h6',
      },
      feature: {
        spacing: { xs: 4, md: 5 },
        titleVariant: isMobile ? 'h2' : 'h1',
        subtitleVariant: isMobile ? 'h5' : 'h4',
      },
    };

    const currentLayout = layoutVariants[variant];
    const alignmentMap = {
      left: 'flex-start',
      center: 'center',
      right: 'flex-end',
    };

    return (
      <Section
        ref={ref}
        variant="hero"
        sx={[
          {
            ...backgroundStyles[backgroundVariant],
            display: 'flex',
            alignItems: 'center',
            textAlign: align,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...sectionProps}
        {...props}
      >
        {/* Video Background */}
        {backgroundVariant === 'video' && videoSrc && (
          <Box
            component="video"
            autoPlay
            muted
            loop
            playsInline
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              minWidth: '100%',
              minHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'cover',
              zIndex: 1,
            }}
          >
            <source src={videoSrc} type="video/mp4" />
          </Box>
        )}

        <Stack
          direction="column"
          alignItems={alignmentMap[align]}
          spacing={currentLayout.spacing}
          sx={{
            width: '100%',
            maxWidth: '800px',
            mx: align === 'center' ? 'auto' : undefined,
          }}
        >
          <Typography
            variant={currentLayout.titleVariant as any}
            component="h1"
            sx={{
              fontWeight: 'bold',
              lineHeight: 1.2,
              maxWidth: '100%',
              // Enhanced styling for brand backgrounds
              ...(backgroundVariant === 'brand' && {
                textShadow:
                  muiTheme.palette.mode === 'light'
                    ? '0 2px 4px rgba(0, 0, 0, 0.1)'
                    : '0 2px 4px rgba(0, 0, 0, 0.3)',
              }),
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant={currentLayout.subtitleVariant as any}
              component="p"
              sx={{
                opacity: 0.9,
                maxWidth: '600px',
                lineHeight: 1.5,
              }}
            >
              {subtitle}
            </Typography>
          )}

          {children}

          {actions.length > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
              alignItems={align === 'center' ? 'center' : 'flex-start'}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || (index === 0 ? 'contained' : 'outlined')}
                  color={action.color || 'primary'}
                  size={action.size || 'large'}
                  onClick={action.onClick}
                  disabled={action.disabled ?? false}
                  startIcon={action.startIcon}
                  endIcon={action.endIcon}
                  sx={{
                    minWidth: { xs: '200px', sm: 'auto' },
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Section>
    );
  },
);

Hero.displayName = 'Hero';

export default Hero;
